import React from 'react';
import { Box, Skeleton, Stack, Toolbar } from '@mui/material';

const PageSkeleton: React.FC = () => {
  const drawerWidth = 240;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Sidebar skeleton - always full width */}
      <Box
        sx={{
          width: { xs: 0, md: drawerWidth },
          flexShrink: 0,
          bgcolor: '#282828',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 1200,
          display: { xs: 'none', md: 'block' }
        }}
      >
        {/* Drawer header with chevron */}
        <Box sx={{
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          pr: 2,
          pl: 2
        }}>
          <Skeleton
            variant="circular"
            width={48}
            height={48}
            sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
            animation="wave"
          />
        </Box>

        {/* Menu items skeleton */}
        <Stack spacing={2} sx={{ p: 2, mt: 2 }}>
          {Array.from({ length: 10 }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton
                variant="circular"
                width={24}
                height={24}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', flexShrink: 0 }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width={`${70 + Math.random() * 30}%`}
                height={24}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }}
                animation="wave"
              />
            </Box>
          ))}
        </Stack>
      </Box>

      {/* App bar skeleton */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          left: { xs: 0, md: drawerWidth },
          zIndex: 1100,
          bgcolor: '#282828',
          height: 80
        }}
      >
        <Toolbar sx={{ height: '100%', px: 3 }}>
          {/* Menu icon for mobile */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mr: 3 }}>
            <Skeleton
              variant="circular"
              width={48}
              height={48}
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
              animation="wave"
            />
          </Box>

          {/* Company name skeleton */}
          <Skeleton
            variant="text"
            width={280}
            height={40}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              flexGrow: 1,
              borderRadius: 1
            }}
            animation="wave"
          />

          {/* User menu skeleton */}
          <Skeleton
            variant="circular"
            width={48}
            height={48}
            sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', ml: 3 }}
            animation="wave"
          />
        </Toolbar>
      </Box>

      {/* Main content skeleton */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          mt: '80px',
          ml: { xs: 0, md: `${drawerWidth}px` },
          minHeight: 'calc(100vh - 80px)'
        }}
      >
        {/* Page title skeleton */}
        <Box sx={{ mb: 5, mt: 2 }}>
          <Skeleton
            variant="text"
            width="80%"
            height={48}
            sx={{
              borderRadius: 1,
              width: { xs: '80%', sm: 400 }
            }}
            animation="wave"
          />
          <Skeleton
            variant="text"
            width="60%"
            height={24}
            sx={{
              borderRadius: 1,
              mt: 1,
              width: { xs: '60%', sm: 250 }
            }}
            animation="wave"
          />
        </Box>

        {/* Content skeleton */}
        <Stack spacing={4}>
          {/* Action buttons skeleton */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 3 }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height={48}
              sx={{
                borderRadius: 2,
                width: { xs: '100%', sm: 140 }
              }}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={48}
              sx={{
                borderRadius: 2,
                width: { xs: '100%', sm: 140 }
              }}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={48}
              sx={{
                borderRadius: 2,
                display: { xs: 'none', sm: 'block' },
                width: { xs: '100%', sm: 100 }
              }}
              animation="wave"
            />
          </Stack>

          {/* Stats cards skeleton */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: 'white',
                  p: 3,
                  borderRadius: 2,
                  boxShadow: 1,
                  flex: 1,
                  minHeight: 120
                }}
              >
                <Skeleton
                  variant="text"
                  width="60%"
                  height={24}
                  sx={{ borderRadius: 1, mb: 1 }}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width="80%"
                  height={40}
                  sx={{ borderRadius: 1 }}
                  animation="wave"
                />
              </Box>
            ))}
          </Stack>

          {/* Main content area skeleton */}
          <Box sx={{ bgcolor: 'white', p: 4, borderRadius: 2, boxShadow: 1 }}>
            {/* Table header */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Skeleton
                variant="text"
                width="50%"
                height={32}
                sx={{
                  borderRadius: 1,
                  width: { xs: '50%', sm: 200 }
                }}
                animation="wave"
              />
              <Box sx={{ flexGrow: 1 }} />
              <Skeleton
                variant="rectangular"
                width={100}
                height={40}
                sx={{
                  borderRadius: 1,
                  width: { xs: 100, sm: 120 }
                }}
                animation="wave"
              />
            </Stack>

            {/* Table content */}
            <Stack spacing={2}>
              {Array.from({ length: 8 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 1,
                    bgcolor: '#f8f9fa'
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    sx={{ mr: 2 }}
                    animation="wave"
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={24}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                      animation="wave"
                    />
                    <Skeleton
                      variant="text"
                      width="50%"
                      height={20}
                      sx={{ borderRadius: 1 }}
                      animation="wave"
                    />
                  </Box>
                  <Skeleton
                    variant="rectangular"
                    width={100}
                    height={36}
                    sx={{ borderRadius: 1, ml: 2 }}
                    animation="wave"
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default PageSkeleton;